import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import MoleculeAuthInput from '../../auth/molecules/MoleculeAuthInput';
import { BlackFullWidthBtn } from '../../../shared/component/headless/button/BtnBody';
import { useGroupMember } from '../../../shared/hooks/useApi/useGroupMember';
import { useGroupMemberStore } from '../../../shared/store/groupMemberStore';
import { useGroupMemberLinkStore } from '../../../shared/store/groupMemberLinkStore';
import { useModalStore } from '../../../shared/store/portal/modalStore';
import { useStandardAlertStore } from '../../../shared/store/portal/alertStore';
import { useLoadingStore } from '../../../shared/store/loadingStore';
import { useValidation } from '../../../shared/hooks/useValidation';
import { getModalMessage } from '../../../shared/constants/modalMessages';
import { getBtnName } from '../../../shared/constants/btnNames';
import { getAlertMessage } from '../../../shared/constants/alertMessages';
import UniversalIconBtn from '../../../shared/component/headless/button/UniversalIconBtn';
import { MobileHeader, Title } from '../../../shared/component/modal/SearchModalLayout';
import belowIcon from '../../../asset/icon/below.png';
import {
  AlertPosition,
  AlertType,
  ModalType,
} from '../../../shared/enum/EnumRepository';

interface GroupMemberLinkModalMobileProps {
  setIsUserInput: (isUserInput: boolean) => void;
}

const GroupMemberLinkModalMobile: React.FC<GroupMemberLinkModalMobileProps> = ({
  setIsUserInput,
}) => {
  const { standardModal, setStandardModal, clearModal } = useModalStore();
  const member = useGroupMemberStore((state) =>
    state.groupMemberList.find((item) => item.id === standardModal?.value),
  );
  const { previewGroupMemberLink } = useGroupMember();
  const { setOverwriteData, clearOverwriteData } = useGroupMemberLinkStore();
  const { enqueueAlert } = useStandardAlertStore();
  const { isLoading } = useLoadingStore();
  const { isEmail } = useValidation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsUserInput(email.length > 0 || password.length > 0);
  }, [email, password, setIsUserInput]);

  useEffect(() => {
    clearOverwriteData();
    modalRef.current?.focus();
  }, [clearOverwriteData]);

  if (!member || !standardModal) return null;

  const changeEmailInput = (value: string) => {
    setEmailError(
      value.length > 0 && !isEmail(value)
        ? 'Invalid email format'
        : '',
    );
    setEmail(value);
  };

  const changePasswordInput = (value: string) => {
    setPasswordError('');
    setPassword(value);
  };

  const handleConnect = async () => {
    if (email === '' || password === '') {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('MISSING_REQUIRED_FIELD'),
      });
      return;
    }
    if (emailError || passwordError) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('CHECK_FORMAT'),
      });
      return;
    }

    const result = await previewGroupMemberLink({
      id: member.id,
      email,
      password,
    });
    if (!result) return;

    setOverwriteData(result);
    setStandardModal({
      modalType: ModalType.GROUP_MEMBER_LINK_CONFIRM,
      title: null,
      value: member.id,
      handleChange: () => {},
    });
  };

  const handleClose = () => {
    clearOverwriteData();
    clearModal();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (isLoading) return;
    switch (event.key) {
      case 'Enter':
        handleConnect();
        break;
      case 'Escape':
        handleClose();
        break;
      default:
        break;
    }
  };

  return (
    <LinkComp ref={modalRef} onKeyDown={handleKeyDown} tabIndex={-1}>
      <MainContainer>
        <MobileHeader>
          <Title>{getModalMessage('GROUP_MEMBER_LINK_TITLE')}</Title>
          <UniversalIconBtn
            size="fit"
            border={false}
            isTransparent
            backgroundImg={belowIcon}
            onClick={handleClose}
            aria-label="Close"
          />
        </MobileHeader>
        <MoleculeAuthInput
          name={getModalMessage('AUTH_EMAIL_LABEL')}
          value={email}
          handleChange={changeEmailInput}
          err={emailError}
          placeholder={getModalMessage('AUTH_EMAIL_LABEL')}
          nameVisible={false}
          errVisible={false}
        />
        <MoleculeAuthInput
          name={getModalMessage('AUTH_PASSWORD_LABEL')}
          value={password}
          handleChange={changePasswordInput}
          err={passwordError}
          placeholder={getModalMessage('AUTH_PASSWORD_LABEL')}
          nameVisible={false}
          errVisible={false}
        />
        <SubmitButton onClick={handleConnect} disabled={isLoading > 0}>
          {getBtnName('CONNECT_ACCOUNT')}
        </SubmitButton>
      </MainContainer>
    </LinkComp>
  );
};

export default GroupMemberLinkModalMobile;

const LinkComp = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 4px;
  outline: none;
`;

const MainContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SubmitButton = styled(BlackFullWidthBtn)`
  margin: 25px 0 15px;
`;
